import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/features/auth/AuthProvider';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import {
  fetchAllInventory,
  fetchStockMovements,
  fetchStoreInventoryMap,
  mergeWithStoreStock,
  recordStockMovement,
} from '../inventoryQueries';
import {
  bulkCreateSerializedItems,
  fetchSerializedItems,
  generateSerialNumbers,
} from '../serializedQueries';

export function InventoryLedgerTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const currentStoreId = useCurrentStore((s) => s.currentStoreId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [restockQty, setRestockQty] = useState(1);
  const [restockNote, setRestockNote] = useState('');
  const [serialCount, setSerialCount] = useState(1);

  const { data: items = [] } = useQuery({ queryKey: ['inventory'], queryFn: fetchAllInventory });
  const { data: storeMap = {} } = useQuery({
    queryKey: ['store-inventory-map', currentStoreId],
    queryFn: () => (currentStoreId ? fetchStoreInventoryMap(currentStoreId) : Promise.resolve({})),
    enabled: !!currentStoreId,
  });
  const withStock = mergeWithStoreStock(items, storeMap);
  const selected = withStock.find((i) => i.id === selectedId);

  const { data: movements = [] } = useQuery({
    queryKey: ['stock-movements', selectedId, currentStoreId],
    queryFn: () =>
      selectedId && currentStoreId
        ? fetchStockMovements(selectedId, currentStoreId)
        : Promise.resolve([]),
    enabled: !!selectedId && !!currentStoreId,
  });

  const { data: serials = [] } = useQuery({
    queryKey: ['serialized-items', selectedId, currentStoreId],
    queryFn: () =>
      selectedId && currentStoreId
        ? fetchSerializedItems(selectedId, currentStoreId)
        : Promise.resolve([]),
    enabled: !!selectedId && !!currentStoreId,
  });

  const restock = useMutation({
    mutationFn: async () => {
      if (!selectedId || !currentStoreId) throw new Error('Select store and product');
      await recordStockMovement({
        itemId: selectedId,
        storeId: currentStoreId,
        type: 'RESTOCK_IN',
        quantity: restockQty,
        note: restockNote || 'Manual restock',
        actorId: user?.uid,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['store-inventory-map'] });
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      toast({ title: 'Restock recorded', variant: 'success' });
      setRestockNote('');
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  const addSerials = useMutation({
    mutationFn: async () => {
      if (!selected || !currentStoreId) throw new Error('Select product');
      const existing = serials.map((s) => s.serialNumber);
      const numbers = generateSerialNumbers(selected.sku || selected.id, existing, serialCount);
      await bulkCreateSerializedItems(
        numbers.map((sn) => ({ itemId: selected.id, storeId: currentStoreId, serialNumber: sn })),
      );
      await recordStockMovement({
        itemId: selected.id,
        storeId: currentStoreId,
        type: 'RESTOCK_IN',
        quantity: serialCount,
        note: `Serialized restock: ${numbers.join(', ')}`,
        serialNumbers: numbers,
        actorId: user?.uid,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['serialized-items'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['store-inventory-map'] });
      toast({ title: 'Serialized items added', variant: 'success' });
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  if (!currentStoreId) {
    return <p className="p-8 text-center text-sm text-muted-foreground">Select a store from the topbar.</p>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-4">
        <Label className="mb-2 block">Select product</Label>
        <select
          className="w-full h-9 rounded-md border px-2 text-sm"
          value={selectedId ?? ''}
          onChange={(e) => setSelectedId(e.target.value || null)}
        >
          <option value="">Choose…</option>
          {withStock.map((i) => (
            <option key={i.id} value={i.id}>
              {i.sku} — {i.brand} ({i.quantity} in stock)
            </option>
          ))}
        </select>

        {selected && (
          <div className="mt-4 space-y-3">
            <p className="text-sm">
              Current stock at store: <strong>{selected.quantity}</strong>
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label>Restock quantity</Label>
                <Input type="number" min={1} value={restockQty} onChange={(e) => setRestockQty(Number(e.target.value))} />
              </div>
              <div>
                <Label>Note</Label>
                <Input value={restockNote} onChange={(e) => setRestockNote(e.target.value)} placeholder="Supplier delivery…" />
              </div>
            </div>
            <Button variant="gold" disabled={restock.isPending} onClick={() => restock.mutate()}>
              Record restock
            </Button>

            <div className="border-t pt-3">
              <Label>Generate serialized units</Label>
              <div className="mt-2 flex gap-2">
                <Input type="number" min={1} value={serialCount} onChange={(e) => setSerialCount(Number(e.target.value))} />
                <Button variant="outline" disabled={addSerials.isPending} onClick={() => addSerials.mutate()}>
                  Add serials + stock
                </Button>
              </div>
              {serials.length > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {serials.filter((s) => s.isAvailable).length} available / {serials.length} total serials
                </p>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-medium mb-3">Movement history</h3>
        {!selectedId && <p className="text-sm text-muted-foreground">Select a product.</p>}
        <ul className="space-y-2 max-h-[50vh] overflow-y-auto">
          {movements.map((m) => (
            <li key={m.id} className="rounded border px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">{m.type}</span>
                <span className="tabular-nums">±{m.quantity}</span>
              </div>
              <p className="text-xs text-muted-foreground">{m.note}</p>
              {m.applied === false && (
                <p className="text-xs text-amber-600">Pending application…</p>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
